import { logger } from './logger';

interface ErrorContext {
  userId?: string;
  component?: string;
  action?: string;
  additionalData?: Record<string, unknown>;
}

const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  return new Error(String(error));
};

/**
 * Улучшенный обработчик ошибок с контекстом
 */
export class ErrorHandler {
  /**
   * Логирует ошибку с дополнительным контекстом
   */
  static captureError(
    error: Error | string,
    context: ErrorContext = {},
    level: 'error' | 'warning' | 'info' = 'error',
  ) {
    const errorObj = typeof error === 'string' ? new Error(error) : error;

    // Добавляем контекст к ошибке
    if (context.userId) {
      logger.setUser({ id: context.userId });
    }

    if (context.component) {
      logger.setTag('component', context.component);
    }

    if (context.action) {
      logger.setTag('action', context.action);
    }

    if (context.additionalData) {
      logger.setContext('additionalData', context.additionalData);
    }

    // Добавляем breadcrumb для отслеживания действий пользователя
    logger.addBreadcrumb({
      message: `Error in ${context.component || 'unknown component'}`,
      category: 'error',
      level: level,
      data: {
        action: context.action,
        error: errorObj.message,
        stack: errorObj.stack,
      },
    });

    // Логируем ошибку
    logger.captureException(errorObj, {
      tags: {
        component: context.component,
        action: context.action,
        level: level,
      },
      extra: context.additionalData,
    });
  }

  /**
   * Логирует предупреждение
   */
  static captureWarning(message: string, context: ErrorContext = {}) {
    this.captureError(message, context, 'warning');
  }

  /**
   * Логирует информационное сообщение
   */
  static captureInfo(message: string, context: ErrorContext = {}) {
    this.captureError(message, context, 'info');
  }

  /**
   * Оборачивает функцию для автоматического логирования ошибок
   */
  static wrapFunction<TArgs extends unknown[], TResult>(
    fn: (...args: TArgs) => Promise<TResult>,
    context?: ErrorContext,
  ): (...args: TArgs) => Promise<TResult>;

  static wrapFunction<TArgs extends unknown[], TResult>(
    fn: (...args: TArgs) => TResult,
    context?: ErrorContext,
  ): (...args: TArgs) => TResult;

  static wrapFunction<TArgs extends unknown[], TResult>(
    fn: (...args: TArgs) => TResult | Promise<TResult>,
    context: ErrorContext = {},
  ): (...args: TArgs) => TResult | Promise<TResult> {
    return (...args: TArgs) => {
      try {
        const result = fn(...args);

        // Если функция возвращает Promise, обрабатываем его
        if (result instanceof Promise) {
          return result.catch((error: Error) => {
            this.captureError(error, {
              ...context,
              additionalData: { args: args.length },
            });
            throw error;
          });
        }

        return result;
      } catch (error) {
        this.captureError(normalizeError(error), {
          ...context,
          additionalData: { args: args.length },
        });
        throw error;
      }
    };
  }
}

/**
 * React hook для обработки ошибок в компонентах
 */
export function useErrorHandler(componentName: string) {
  return {
    captureError: (
      error: Error | string,
      action?: string,
      additionalData?: Record<string, unknown>,
    ) => {
      ErrorHandler.captureError(error, {
        component: componentName,
        action,
        additionalData,
      });
    },
    captureWarning: (
      message: string,
      action?: string,
      additionalData?: Record<string, unknown>,
    ) => {
      ErrorHandler.captureWarning(message, {
        component: componentName,
        action,
        additionalData,
      });
    },
    captureInfo: (
      message: string,
      action?: string,
      additionalData?: Record<string, unknown>,
    ) => {
      ErrorHandler.captureInfo(message, {
        component: componentName,
        action,
        additionalData,
      });
    },
  };
}
